import { createApi } from '@reduxjs/toolkit/query/react';
import {
  AssignCrmAppTaskDto,
  AssignEmployeeTicketRequest,
  ChangeStatus,
  CmisRequestProcessingProgressEvent,
  CmisServicesRequestData,
  CmisServicesResponseData,
  CollaborationRequest,
  CompleteCollaborationTicketRequest,
  CompleteTickets,
  CreateObstacleTicketRequest,
  GetFieldEmployeesResponse,
  GetFieldTasksResponse,
  InprogressTickets,
  Option,
  OptionDetail,
  OrganizationUnitChildren,
  OrganizationUnitService,
  RequestData,
  ResponseDto,
  ResponsePagingDto,
  TicketCountRequest,
  TicketDetail,
  TransferCmis,
  TransferSupportUnit,
  TransferTicketRequest,
  TransferToUnit,
  UpdateObstacleTicketRequest,
  UserInOrganizationUnitResponse
} from 'types';
import axiosBaseQuery from 'utils/base-api';

export const requestForm = createApi({
  reducerPath: 'requestForm',
  tagTypes: ['pending', 'inprogress', 'collaboration', 'completed'],
  baseQuery: axiosBaseQuery,
  endpoints: (builder) => ({
    getPendingTicket: builder.query<ResponsePagingDto<TicketDetail>, number>({
      query: (pageSize) => ({
        url: '/tickets/dienluc/pending_reception',
        method: 'get',
        params: { pageSize }
      })
    }),

    getTicketDetail: builder.query<ResponseDto<TicketDetail>, number>({
      query: (ticketDetail) => ({
        url: `/tickets/detail/${ticketDetail}`,
        method: 'get'
        // params: { ticketDetail }
      })
    }),
    getTicketCount: builder.query<ResponseDto<number>, TicketCountRequest>({
      query: ({ status, isObstacleTickets, fromDate, toDate, isPriority }) => {
        const url = `/tickets/dienluc/${status}/count`;

        const params: Record<string, any> = {};
        if (isObstacleTickets === true) params.isObstacleTickets = true;
        if (fromDate) params.fromDate = fromDate;
        if (toDate) params.toDate = toDate;
        if (isPriority) params.isPriority = isPriority;

        return {
          url,
          method: 'get',
          params,
          _ignoreNotificationError: true
        };
      }
    }),

    getTicketList: builder.query<ResponsePagingDto<TicketDetail>, RequestData>({
      query: (requestData) => {
        const params = new URLSearchParams();

        Object.entries(requestData).forEach(([key, value]) => {
          if (key === 'isObstacleTickets' && value === true) {
            params.append(key, value.toString());
          } else if (
            value !== undefined &&
            value !== null &&
            value !== '' &&
            value !== 0 &&
            key !== 'isObstacleTickets'
          ) {
            if (Array.isArray(value)) {
              value.forEach((v) => params.append(key, v.toString()));
            } else {
              params.append(key, value.toString());
            }
          }
        });

        const url =
          requestData.status === 'obstacle' ? `/tickets/dienluc/in_progress` : `/tickets/dienluc/${requestData.status}`;

        return {
          url,
          method: 'get',
          params: params
        };
      }
    }),

    getObstacleTicket: builder.query<ResponsePagingDto<TicketDetail>, RequestData>({
      query: (requestData) => {
        const params = new URLSearchParams();

        Object.entries(requestData).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '' && value !== 0) {
            if (Array.isArray(value)) {
              value.forEach((v) => params.append(key, v.toString()));
            } else {
              params.append(key, value.toString());
            }
          }
        });

        return {
          url: '/tickets/dienluc/in_progress',
          method: 'get',
          params: params
        };
      }
    }),

    //Get Transfer CMIS Services
    getCmisServices: builder.query<ResponsePagingDto<CmisServicesResponseData>, CmisServicesRequestData>({
      query: (requestData) => {
        const params = new URLSearchParams();

        Object.entries(requestData).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '' && value !== 0) {
            if (Array.isArray(value)) {
              value.forEach((v) => params.append(key, v.toString()));
            } else {
              params.append(key, value.toString());
            }
          }
        });

        return {
          url: '/service_types_cmis',
          method: 'get',
          params: params
        };
      }
    }),

    //Organization
    getOrganizationUnitChildren: builder.query<ResponsePagingDto<OrganizationUnitChildren>, number>({
      query: (organizationUnitId) => {
        return {
          url: `/organization_units/${organizationUnitId}/children`,
          method: 'get'
        };
      }
    }),
    getOrganizationUnitDetailServices: builder.query<ResponsePagingDto<OrganizationUnitService>, number>({
      query: (organizationUnitId) => {
        return {
          url: `/organization_units/${organizationUnitId}/service_types/options?isActive=true`,
          method: 'get',
          params: {
            pageIndex: 1,
            pageSize: 200
          }
        };
      }
    }),
    getOptionDetail: builder.query<ResponsePagingDto<OptionDetail>, number>({
      query: (optionTypeId) => {
        return {
          url: `/options`,
          method: 'get',
          params: { optionTypeId, service: 'CORE' }
        };
      }
    }),
    getUserInOrganizationUnit: builder.query<ResponsePagingDto<UserInOrganizationUnitResponse>, number>({
      query: (organizationUnitId) => {
        return {
          url: `/organization_units/${organizationUnitId}/users?pageIndex=1&pageSize=100`,
          method: 'get'
        };
      }
    }),
    getReasonOutage: builder.query<ResponsePagingDto<Option>, number>({
      query: (optionTypeId) => {
        return {
          url: `/options?service=CORE&optionTypeId=${optionTypeId}`,
          method: 'get'
        };
      }
    }),
    getCustomerRequestHistory: builder.query<ResponseDto<TicketDetail[]>, string>({
      query: (customerCode) => {
        return {
          url: `/tickets/dienluc/customer_request_history/${customerCode}`,
          method: 'get'
        };
      }
    }),
    getCmisProcessingProgress: builder.query<ResponseDto<CmisRequestProcessingProgressEvent[]>, string>({
      query: (requestCode) => {
        return {
          url: `/cmis_info_retrieval/customer_request_processing_progress?requestCode=${requestCode}`,
          method: 'get'
        };
      }
    }),

    getFieldEmployees: builder.query<GetFieldEmployeesResponse, string>({
      query: (teamCode: string) => ({
        url: '/crm_app_integration/field_employees',
        method: 'GET',
        params: { teamCode }
      }),
      transformResponse: (response: any): GetFieldEmployeesResponse => response.data
    }),

    getFieldTasks: builder.query<GetFieldTasksResponse, string>({
      query: (teamCode: string) => ({
        url: '/crm_app_integration/field_tasks',
        method: 'GET',
        params: { teamCode }
      }),
      transformResponse: (response: any): GetFieldTasksResponse => response.data
    }),

    getFieldTaskList: builder.query<ResponseDto<GetFieldTasksResponse>, string>({
      query: (teamCode: string) => ({
        url: '/crm_app_integration/field_tasks',
        method: 'GET',
        params: { teamCode }
      })
    }),

    //! Patch API

    //? Priority Ticket - Turn off ticket priority
    patchTurnOffTicketPriority: builder.mutation<ResponseDto<null>, number>({
      query: (ticketId) => {
        return {
          url: `/tickets/turn_off_ticket_priority/${ticketId}`,
          method: 'patch'
        };
      }
    }),

    //  Chuyển phiếu đến đơn vị hỗ trợ - (ĐLuc)
    patchTransferSupportUnit: builder.mutation<ResponseDto<null>, TransferSupportUnit>({
      query: (requestData) => {
        return {
          url: `/tickets/dienluc/transfer_support_unit/${requestData.ticketId}`,
          method: 'patch',
          data: requestData
        };
      }
    }),

    patchTransferTo: builder.mutation<ResponseDto<null>, TransferToUnit>({
      query: ({ toUnit, ticketId }) => {
        return {
          url: `/tickets/dtv/transfer_to_${toUnit}/${ticketId}`,
          method: 'patch'
        };
      }
    }),
    patchReceptionTicket: builder.mutation<ResponseDto<ChangeStatus>, number>({
      query: (ticketId) => {
        return {
          url: `/tickets/dienluc/reception/${ticketId}`,
          method: 'patch'
        };
      }
    }),
    patchCompleteTicket: builder.mutation<ResponseDto<ChangeStatus>, CompleteTickets>({
      query: (requestData) => {
        return {
          url: `/tickets/dienluc/complete/${requestData.ticketId}`,
          method: 'patch',
          data: requestData
        };
      }
    }),
    patchInprogressTicket: builder.mutation<ResponseDto<ChangeStatus>, InprogressTickets>({
      query: (requestData) => {
        return {
          url: `/tickets/dienluc/in_progress/${requestData.ticketId}`,
          method: 'patch',
          data: requestData
        };
      }
    }),
    patchCollaborationTickets: builder.mutation<ResponseDto<ChangeStatus>, CollaborationRequest>({
      query: ({ ticketId, collaborationDetail }) => {
        return {
          url: `/tickets/dienluc/collaboration/${ticketId}`,
          method: 'patch',
          data: collaborationDetail
        };
      }
    }),
    patchTranferTickets: builder.mutation<ResponseDto<ChangeStatus>, TransferTicketRequest>({
      query: ({ ticketId, transferDetail }) => {
        return {
          url: `/tickets/dienluc/transfer/${ticketId}`,
          method: 'patch',
          data: transferDetail
        };
      }
    }),
    patchNewObstacleTicket: builder.mutation<ResponseDto<ChangeStatus>, CreateObstacleTicketRequest>({
      query: (obstacleTicket) => {
        return {
          url: `/ticket_hold_manager`,
          method: 'post',
          data: obstacleTicket
        };
      }
    }),
    patchTransferToCustomerCareCenter: builder.mutation<ResponseDto<ChangeStatus>, InprogressTickets>({
      query: ({ ticketId, processingContent }) => {
        return {
          url: `/tickets/dienluc/return/${ticketId}`,
          method: 'patch',
          data: { processingContent }
        };
      }
    }),
    patchUpdateObstacleTicket: builder.mutation<ResponseDto<ChangeStatus>, UpdateObstacleTicketRequest>({
      query: (request) => {
        return {
          url: `/ticket_hold_manager/${request.ticketHoldManagerId}`,
          method: 'patch',
          data: request
        };
      }
    }),
    patchCompleteObstacleTicket: builder.mutation<ResponseDto<ChangeStatus>, UpdateObstacleTicketRequest>({
      query: (request) => {
        return {
          url: `/ticket_hold_manager/close/${request.ticketHoldManagerId}`,
          method: 'patch',
          data: request
        };
      }
    }),
    patchCompleteCollaborationTicket: builder.mutation<ResponseDto<ChangeStatus>, CompleteCollaborationTicketRequest>({
      query: (request) => {
        return {
          url: `/tickets/dienluc/collaboration/${request.ticketId}/completed`,
          method: 'patch',
          data: request
        };
      }
    }),
    patchTransferToCmis: builder.mutation<ResponseDto<ChangeStatus>, TransferCmis>({
      query: (request) => {
        return {
          url: `/cmis_crm_integration/send_request`,
          method: 'post',
          data: request
        };
      }
    }),
    patchAssignEmployee: builder.mutation<ResponseDto<null>, AssignEmployeeTicketRequest>({
      query: (request) => {
        return {
          url: `/tickets/dienluc/assignee/${request.ticketId}`,
          method: 'patch',
          data: {
            assigneeId: request.assigneeId
          }
        };
      }
    }),
    patchAssignCrmAppTask: builder.mutation<ResponseDto<null>, AssignCrmAppTaskDto>({
      query: (request) => ({
        url: '/crm_app_integration/assign_task',
        method: 'PATCH',
        data: request
      })
    })
  })
});

export const {
  useGetTicketCountQuery,
  useLazyGetTicketCountQuery,

  useGetPendingTicketQuery,
  useGetTicketDetailQuery,
  useGetTicketListQuery,
  useLazyGetTicketListQuery,

  useGetOrganizationUnitChildrenQuery,
  useGetOrganizationUnitDetailServicesQuery,
  useGetOptionDetailQuery,
  useGetObstacleTicketQuery,
  useGetCmisServicesQuery,
  useGetUserInOrganizationUnitQuery,
  useGetReasonOutageQuery,
  useGetCustomerRequestHistoryQuery,
  useGetCmisProcessingProgressQuery,
  useGetFieldEmployeesQuery,
  useGetFieldTasksQuery,
  useGetFieldTaskListQuery,

  usePatchTurnOffTicketPriorityMutation, //? Turn off Ticket Priority
  usePatchTransferSupportUnitMutation,
  usePatchReceptionTicketMutation,
  usePatchCompleteTicketMutation,
  usePatchInprogressTicketMutation,
  usePatchCollaborationTicketsMutation,
  usePatchNewObstacleTicketMutation,
  usePatchTransferToCustomerCareCenterMutation,
  usePatchUpdateObstacleTicketMutation,
  usePatchCompleteObstacleTicketMutation,
  usePatchTransferToCmisMutation,
  usePatchAssignEmployeeMutation,
  usePatchCompleteCollaborationTicketMutation,
  usePatchTranferTicketsMutation,
  usePatchAssignCrmAppTaskMutation
} = requestForm;
